import "./index.css";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { clsx } from "clsx";
import { Tooltip } from "../Tooltip";
import type { ITruncatedContentProps } from "./types";

const CLAMP_EPSILON = 0.5;

export const TruncatedContent = ({
  children,
  content,
  maxLines = 1,
  as,
  className,
  style: outerStyle = {},
  tooltipProps,
}: ITruncatedContentProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const [isClamped, setIsClamped] = useState(false);
  const Element = as || "div";

  const style = useMemo<CSSProperties>(() => {
    if (maxLines <= 1) {
      return {
        display: "block",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        ...outerStyle,
      };
    }
    return {
      overflow: "hidden",
      lineClamp: String(maxLines),
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: String(maxLines),
      ...outerStyle,
    };
  }, [maxLines, outerStyle]);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    if (maxLines <= 1) {
      const clamped = el.scrollWidth - el.clientWidth > CLAMP_EPSILON;
      setIsClamped(clamped);
    } else {
      const clamped = el.scrollHeight - el.clientHeight > CLAMP_EPSILON;
      setIsClamped(clamped);
    }
  }, [maxLines]);

  useLayoutEffect(() => {
    measure();
  }, [children, content, maxLines, measure]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const contentJSX = children || content || "";

  const trigger = (
    <Element
      ref={ref}
      aria-label={
        isClamped && typeof contentJSX === "string" ? contentJSX : undefined
      }
      className={clsx("nuco-truncated-content", className)}
      style={style}
    >
      {contentJSX}
    </Element>
  );

  return isClamped ? (
    <Tooltip {...tooltipProps} content={contentJSX}>
      {trigger}
    </Tooltip>
  ) : (
    trigger
  );
};
